package com.banyan.flightviewer.data

/**
 * Airline lookup from ICAO 3-letter designators (callsign prefixes).
 * Covers the top ~200 airlines worldwide.
 */
object AirlineDatabase {

    private val airlines: Map<String, String> = mapOf(
        // US majors
        "AAL" to "American Airlines",
        "DAL" to "Delta Air Lines",
        "UAL" to "United Airlines",
        "SWA" to "Southwest Airlines",
        "JBU" to "JetBlue Airways",
        "ASA" to "Alaska Airlines",
        "NKS" to "Spirit Airlines",
        "FFT" to "Frontier Airlines",
        "HAL" to "Hawaiian Airlines",
        "AAY" to "Allegiant Air",
        "SKW" to "SkyWest Airlines",
        "RPA" to "Republic Airways",
        "ENY" to "Envoy Air",
        "PDT" to "Piedmont Airlines",
        "JIA" to "PSA Airlines",
        "MES" to "Mesa Airlines",
        "CPZ" to "Compass Airlines",
        "GJS" to "GoJet Airlines",
        "EJA" to "NetJets",
        "LXJ" to "Flexjet",

        // US cargo
        "FDX" to "FedEx Express",
        "UPS" to "UPS Airlines",
        "ABX" to "ABX Air",
        "GTI" to "Atlas Air",
        "PAC" to "Polar Air Cargo",
        "KFS" to "Kalitta Air",

        // Canadian
        "ACA" to "Air Canada",
        "WJA" to "WestJet",
        "TSC" to "Air Transat",
        "POE" to "Porter Airlines",
        "JZA" to "Jazz Aviation",
        "SWG" to "Sunwing Airlines",
        "ROU" to "Rouge (Air Canada)",

        // European majors
        "BAW" to "British Airways",
        "DLH" to "Lufthansa",
        "AFR" to "Air France",
        "KLM" to "KLM Royal Dutch",
        "EIN" to "Aer Lingus",
        "SAS" to "Scandinavian Airlines",
        "FIN" to "Finnair",
        "IBE" to "Iberia",
        "TAP" to "TAP Air Portugal",
        "SWR" to "Swiss Intl Air Lines",
        "AUA" to "Austrian Airlines",
        "BEL" to "Brussels Airlines",
        "LOT" to "LOT Polish Airlines",
        "CSA" to "Czech Airlines",
        "AZA" to "ITA Airways",
        "THY" to "Turkish Airlines",
        "AEE" to "Aegean Airlines",
        "ROT" to "TAROM",
        "JKK" to "Icelandair",
        "ICE" to "Icelandair",
        "NAX" to "Norwegian Air",
        "NOZ" to "Norwegian Air",
        "DY"  to "Norwegian Air",

        // European LCCs
        "RYR" to "Ryanair",
        "EZY" to "easyJet",
        "WZZ" to "Wizz Air",
        "EWG" to "Eurowings",
        "VLG" to "Vueling",
        "BEE" to "Flybe",
        "TVF" to "Transavia France",
        "TRA" to "Transavia",

        // Middle East
        "UAE" to "Emirates",
        "ETD" to "Etihad Airways",
        "QTR" to "Qatar Airways",
        "SVA" to "Saudi Arabian Airlines",
        "GFA" to "Gulf Air",
        "OMA" to "Oman Air",
        "RJA" to "Royal Jordanian",
        "MEA" to "Middle East Airlines",
        "KAC" to "Kuwait Airways",
        "FDB" to "flydubai",
        "AXB" to "Air India Express",

        // Asian majors
        "CPA" to "Cathay Pacific",
        "SIA" to "Singapore Airlines",
        "ANA" to "All Nippon Airways",
        "JAL" to "Japan Airlines",
        "KAL" to "Korean Air",
        "AAR" to "Asiana Airlines",
        "CAL" to "China Airlines",
        "EVA" to "EVA Air",
        "THA" to "Thai Airways",
        "MAS" to "Malaysia Airlines",
        "GIA" to "Garuda Indonesia",
        "CES" to "China Eastern",
        "CSN" to "China Southern",
        "CCA" to "Air China",
        "HDA" to "China Southern (Hebei)",
        "AIJ" to "Interjet",
        "VNL" to "VietJet Air",
        "HVN" to "Vietnam Airlines",
        "PAL" to "Philippine Airlines",
        "CEB" to "Cebu Pacific",
        "AIC" to "Air India",

        // Asian LCCs
        "AXM" to "AirAsia",
        "APJ" to "Peach Aviation",
        "JST" to "Jetstar",
        "JJP" to "Jetstar Japan",
        "TWB" to "T'way Air",
        "ABL" to "Air Busan",
        "ESR" to "Eastar Jet",
        "INM" to "IndiGo",

        // Oceania
        "QFA" to "Qantas",
        "ANZ" to "Air New Zealand",
        "VOZ" to "Virgin Australia",
        "FJI" to "Fiji Airways",

        // Latin America
        "TAM" to "LATAM Brasil",
        "LAN" to "LATAM Chile",
        "AVA" to "Avianca",
        "AMX" to "Aeromexico",
        "CMP" to "Copa Airlines",
        "GLO" to "GOL Linhas Aereas",
        "AEA" to "Air Europa",
        "ARE" to "Aires Colombia",
        "ARG" to "Aerolineas Argentinas",
        "BOV" to "Boliviana de Aviacion",

        // African
        "SAA" to "South African Airways",
        "ETH" to "Ethiopian Airlines",
        "KQA" to "Kenya Airways",
        "RAM" to "Royal Air Maroc",
        "MSR" to "EgyptAir",
        "NIG" to "Arik Air",
        "RWD" to "RwandAir",

        // Cargo international
        "CLX" to "Cargolux",
        "QYC" to "Qatar Airways Cargo",
        "BOX" to "AeroLogic",
        "MPH" to "Martinair Cargo",
        "GEC" to "Lufthansa Cargo",
        "SQC" to "Singapore Airlines Cargo",
        "CAO" to "Air China Cargo",
        "CKK" to "China Cargo Airlines",

        // Business/charter
        "VJT" to "VistaJet",
        "GAJ" to "Gulfstream Aerospace",
        "EJM" to "ExcelAire",
        "XOJ" to "XOJET",
        "TVP" to "TAG Aviation",

        // Military/government
        "RCH" to "US Air Force",
        "AIO" to "US Army",
        "CNV" to "US Navy",
        "CFC" to "Canadian Forces",
        "RRR" to "Royal Air Force",
        "GAF" to "German Air Force",
        "FAF" to "French Air Force",
        "IAM" to "Italian Air Force",
    )

    /**
     * Get the airline name from a callsign. Tries the 3-letter ICAO prefix first,
     * then 2-letter. Returns null if no match found.
     */
    fun getAirlineName(callsign: String): String? {
        if (callsign.length < 2) return null
        // Try 3-letter ICAO prefix
        if (callsign.length >= 3) {
            val prefix3 = callsign.substring(0, 3).uppercase()
            airlines[prefix3]?.let { return it }
        }
        // Try 2-letter prefix
        val prefix2 = callsign.substring(0, 2).uppercase()
        return airlines[prefix2]
    }
}
